import { ROUTES } from "./routes";

export type Faq = {
  id: string;
  q: string;
  a: string;
  paths: string[];
  cta?: { label: string; to: string };
};

const {
  HOME,
  INVOICES,
  SETTLEMENTS,
  WHATSAPP,
  CUSTOMERS,
  PAYOUTS,
  BILLING,
  SUBSCRIPTIONS,
  TUTORIALS,
} = ROUTES.DASHBOARD;

export const FAQS: Faq[] = [
  {
    id: "archivado",
    q: "¿Qué significa el estado Archivado?",
    a: "Es un recaudo anulado: ya no aparece en el portal de pagos y el inquilino no puede pagarlo. Queda así cuando lo archivas desde el menú de la fila o cuando tu ERP lo anula o lo reemplaza por otro. No se puede desarchivar: si el inquilino aún debe ese valor, crea el recaudo de nuevo desde el ERP, por archivo o con Crear factura.",
    paths: [INVOICES],
  },
  {
    id: "ocultar-recaudo",
    q: "¿Cómo evito que el inquilino pague un recaudo?",
    a: "Si lo creaste manualmente o por archivo, abre el menú de la fila y elige Archivar. Si viene del ERP, anúlalo o ajústalo en el ERP y el cambio se refleja en la próxima sincronización. Un administrador también puede archivar recaudos del ERP desde aquí.",
    paths: [INVOICES],
  },
  {
    id: "erp-no-aparecen",
    q: "Creé facturas en el ERP y no aparecen todas en Recaudos",
    a: "Palomma importa las facturas del ERP de forma diaria en la mañana y, además, cada vez que un inquilino consulta su documento en el portal de pagos. Una factura recién creada aparece en la siguiente sincronización o apenas el inquilino la consulte. Si ves un aviso de error de sincronización, revisa con tu proveedor de ERP que la URL y las credenciales de la integración sigan vigentes.",
    paths: [INVOICES],
  },
  {
    id: "reversado",
    q: "¿Qué significa Reversado?",
    a: "El pago fue aprobado y luego el banco o la franquicia lo devolvió, por ejemplo por un contracargo. Ese dinero no cuenta como recaudo: si ya se te había abonado, se descuenta del siguiente abono y lo verás en Conciliación.",
    paths: [INVOICES],
  },
  {
    id: "contabilizacion-erp",
    q: "El pago aparece Pagado aquí pero no se contabilizó en mi ERP",
    a: "Palomma le notifica cada pago al ERP; la columna de contabilización muestra si llegó. Si quedó con error, usa Reintentar Contabilización en el menú de la fila, o Marcar Contabilizado si ya lo registraste a mano en el ERP.",
    paths: [INVOICES],
  },
  {
    id: "comprobante-pago",
    q: "¿Cómo descargo el comprobante de un pago?",
    a: "En el menú de la fila de un recaudo pagado elige Descargar y obtienes el recibo del pago. En un recaudo sin pagar el mismo botón descarga la factura. Con Información de Pago ves el medio, la fecha y la referencia.",
    paths: [INVOICES],
  },
  {
    id: "documento-no-aparece",
    q: "El inquilino dice que su documento no aparece en el portal de pagos",
    a: "El portal muestra los recaudos Sin Pagar cuyo documento coincide exactamente con el del recaudo: sin puntos ni dígito de verificación, y con el tipo correcto si es cédula de extranjería, pasaporte o NIT. Revisa el documento en la fila y corrígelo con Información si hace falta.",
    paths: [INVOICES],
  },
  {
    id: "pago-rechazado",
    q: "Al inquilino le rechazan el pago",
    a: "Con PSE o Nequi el rechazo lo decide su banco; pídele que confirme saldo, topes y que la transacción no haya quedado pendiente en la app del banco. Con tarjeta lo decide el banco emisor o la verificación de seguridad, y las tarjetas internacionales se aceptan pero suelen exigir verificación adicional. En Pagos Fallidos del menú de la fila ves cada intento y su motivo.",
    paths: [INVOICES],
  },
  {
    id: "recargo-tarjeta",
    q: "¿Qué le cuesta al inquilino pagar con tarjeta?",
    a: "Un recargo del 4,9% sobre el valor del recaudo, que el portal le muestra antes de confirmar. Cubre las comisiones de la franquicia y no es un ingreso de tu inmobiliaria. Con PSE no hay recargo para el inquilino.",
    paths: [INVOICES],
  },
  {
    id: "pago-en-orden",
    q: "¿Puedo obligar a que paguen las facturas en orden?",
    a: "Sí: se puede configurar que un inquilino no pueda pagar un mes sin haber pagado los anteriores. Escríbenos y lo activamos para tu comercio.",
    paths: [INVOICES],
  },
  {
    id: "carga-masiva",
    q: "¿Cómo cargo recaudos de forma masiva?",
    a: "Con Crear factura puedes subir un archivo con la plantilla de Excel y crear muchos recaudos a la vez. Si tienes integración con el ERP no hace falta: los recaudos llegan solos.",
    paths: [INVOICES],
    cta: { label: "Ver tutorial", to: TUTORIALS },
  },
  {
    id: "reporte-por-cliente",
    q: "¿Cómo saco un reporte de recaudos por cliente?",
    a: "Filtra por estado y fechas y usa Descargar Excel: el archivo trae cliente, documento, valor, fecha y referencia de cada recaudo. Para cruzar contra tu extracto bancario usa el Reporte de pagos de Conciliación con Fecha de Abono.",
    paths: [INVOICES],
  },
  {
    id: "cuando-llega-dinero",
    q: "El inquilino ya pagó, ¿cuándo llega el dinero a mi cuenta?",
    a: "Los pagos por PSE se abonan en los ciclos ACH establecidos: hay cinco cortes al día (10:00 a.m., 12:30 p.m., 3:00 p.m., 5:00 p.m. y 6:45 p.m.) y cada pago entra en el abono del ciclo siguiente a la hora en que pagó el inquilino. Abre el abono para ver qué facturas lo componen y el valor neto que llegó.",
    paths: [SETTLEMENTS],
  },
  {
    id: "abono-menor",
    q: "¿Por qué el abono es menor que lo que pagaron los inquilinos?",
    a: "Del abono se descuentan la comisión del medio de pago con su IVA y, si los hubo, pagos reversados de abonos anteriores. El detalle del abono lo muestra factura por factura.",
    paths: [SETTLEMENTS],
  },
  {
    id: "conciliar-extracto",
    q: "¿Cómo concilio contra el extracto bancario?",
    a: "Descarga el Reporte de pagos eligiendo Fecha de Abono: agrupa los pagos por el día en que el dinero entró a tu cuenta, que es lo que ves en el extracto. Fecha de Pago sirve para cartera, porque agrupa por el día en que pagó el inquilino.",
    paths: [SETTLEMENTS],
  },
  {
    id: "falta-pago-abono",
    q: "Falta un pago dentro de un abono",
    a: "Verifica primero en Recaudos que el pago esté en estado Pagado y no en Reversado. Si está pagado y no aparece en ningún abono, escríbenos con el ID del pago, la fecha y el valor y lo revisamos.",
    paths: [SETTLEMENTS],
  },
  {
    id: "whatsapp-no-envia",
    q: "¿Por qué no se están enviando los recordatorios?",
    a: "Los mensajes salen todos los días a las 9:00 a.m. solo para inquilinos que tienen celular registrado en Clientes y recaudos Sin Pagar con fecha de vencimiento. Revisa en Configuración de Whatsapp que los envíos estén activos y que los inquilinos tengan celular. Si todo está bien y la tabla sigue sin envíos, escríbenos.",
    paths: [WHATSAPP],
  },
  {
    id: "whatsapp-cuando",
    q: "¿Cuándo se envía cada mensaje?",
    a: "Depende de las reglas activas de tu comercio: Inicial al ciclo siguiente de crear el recaudo, Recordatorio el día hábil antes del vencimiento y Recuperación el día hábil antes de la última fecha de pago. Las reglas activas las ves en Configuración de Whatsapp.",
    paths: [WHATSAPP],
  },
  {
    id: "whatsapp-reglas",
    q: "¿Puedo cambiar las reglas o el texto de los mensajes?",
    a: "Por ahora las reglas las configura Palomma y el texto es una plantilla aprobada por Meta, así que no se edita desde el portal. Escríbenos para activar, pausar o cambiar las reglas de tu comercio.",
    paths: [WHATSAPP],
  },
  {
    id: "whatsapp-error",
    q: "¿Qué significa el estado Error?",
    a: "El mensaje no se pudo entregar. Casi siempre el número no tiene WhatsApp o está mal registrado: corrígelo en Clientes con el lápiz de la fila.",
    paths: [WHATSAPP],
  },
  {
    id: "whatsapp-costo",
    q: "¿Cuánto cuesta cada mensaje?",
    a: "La comisión de cada envío aparece en la columna Comisión y se cobra en la cuenta de cobro mensual que ves en Facturación.",
    paths: [WHATSAPP],
  },
  {
    id: "agregar-inquilino",
    q: "¿Cómo agrego un inquilino?",
    a: "Los inquilinos se crean solos con su primer recaudo, ya sea desde el ERP, por archivo o con Crear factura en Recaudos. Aquí solo se consultan y se corrigen sus datos.",
    paths: [CUSTOMERS],
  },
  {
    id: "editar-inquilino",
    q: "¿Cómo actualizo el celular o el correo de un inquilino?",
    a: "Con el lápiz de la fila. El celular es el que usan los recordatorios de WhatsApp, así que mantenlo al día.",
    paths: [CUSTOMERS],
  },
  {
    id: "inquilino-extranjero",
    q: "El inquilino tiene cédula de extranjería, pasaporte o NIT",
    a: "Registra el tipo y el número exactos en el recaudo: el portal de pagos lo busca con ese documento y no lo encuentra si el tipo no coincide.",
    paths: [CUSTOMERS],
  },
  {
    id: "inquilino-sin-whatsapp",
    q: "Un inquilino no recibe los recordatorios de WhatsApp",
    a: "Verifica que tenga celular registrado y que sea un número con WhatsApp. Los envíos se hacen solo a inquilinos con recaudos Sin Pagar.",
    paths: [CUSTOMERS],
    cta: { label: "Ver envíos", to: WHATSAPP },
  },
  {
    id: "girar-propietarios",
    q: "¿Cómo giro dinero a los propietarios?",
    a: "Descarga la plantilla, llénala con los giros y cárgala como lote; necesitas permiso de dispersión. Cada archivo queda como un lote en la pestaña Lotes y cada giro en Detalle.",
    paths: [PAYOUTS],
  },
  {
    id: "saldo-disponible",
    q: "¿Qué diferencia hay entre saldo disponible y comprometido?",
    a: "El disponible es lo que puedes girar hoy. El comprometido ya está asignado a lotes en curso y se libera si algún giro es devuelto.",
    paths: [PAYOUTS],
  },
  {
    id: "giro-rechazado",
    q: "¿Por qué un giro quedó rechazado?",
    a: "El banco lo devuelve cuando la cuenta no existe, el tipo de cuenta no coincide o el documento del titular no corresponde. Revisa el número tal como aparece en el certificado bancario (sin ceros adicionales), corrige el dato y vuelve a cargarlo; el dinero devuelto regresa al saldo.",
    paths: [PAYOUTS],
  },
  {
    id: "giro-cuando-llega",
    q: "¿Cuándo le llega el dinero al propietario?",
    a: "A cuentas Bancolombia en minutos después de procesar el lote. A otros bancos va por ACH y suele llegar el mismo día hábil o el siguiente.",
    paths: [PAYOUTS],
  },
  {
    id: "comprobante-giro",
    q: "¿Cómo descargo el comprobante de un giro?",
    a: "En la pestaña Detalle, en la fila de cada giro.",
    paths: [PAYOUTS],
  },
  {
    id: "cuenta-de-cobro",
    q: "¿Qué es la cuenta de cobro que me llegó de Palomma?",
    a: "Es lo que Palomma cobra por el servicio de cada mes: comisiones de recaudo, mensajes de WhatsApp y giros. Si dice Descontada, ya se descontó de tus abonos y no tienes nada por pagar; si dice Mes Vencido, se paga aparte. Abre la fila para ver el detalle.",
    paths: [BILLING],
  },
  {
    id: "pagar-cuenta",
    q: "¿Cómo pago una cuenta pendiente?",
    a: "Con el botón Pagar de su fila. Te lleva al portal de pagos y puedes pagar por PSE o tarjeta.",
    paths: [BILLING],
  },
  {
    id: "correo-facturacion",
    q: "¿A qué correo llega la factura electrónica?",
    a: "Al correo de facturación que registraste al vincular tu comercio. Si quieres cambiarlo, escríbenos.",
    paths: [BILLING],
  },
  {
    id: "cobro-por-transaccion",
    q: "¿Palomma cobra por transacción o una mensualidad?",
    a: "Por transacción. Según tu plan, la comisión se descuenta de cada abono o se cobra al mes vencido en esta cuenta de cobro. Las comisiones por medio de pago las ves en Configuración, Métodos de pago.",
    paths: [BILLING],
  },
  {
    id: "palomma-plus",
    q: "Un inquilino pregunta por un cobro de $9.900 que no es el canon",
    a: "Es Palomma Plus, una suscripción mensual opcional que el inquilino elige al pagar y que le da beneficios como Puntos Colombia y descuentos. No es un cobro de tu inmobiliaria ni una comisión del portal. En la pestaña Suscriptores ves quiénes la tienen activa y desde cuándo.",
    paths: [SUBSCRIPTIONS],
  },
  {
    id: "invitados-plus",
    q: "¿Cómo agrego usuarios de mi equipo o invitados a Palomma Plus?",
    a: "Por cada suscripción activa tu comercio gana cupos para darles los beneficios de Palomma Plus a propietarios y colaboradores. Con permisos, los asignas desde Agregar invitados en la pestaña Invitados. Los usuarios que entran al portal se invitan aparte, en Configuración, Usuarios.",
    paths: [SUBSCRIPTIONS],
  },
  {
    id: "cancelar-plus",
    q: "¿Cómo cancela un inquilino su suscripción?",
    a: "Él mismo, desde la pestaña Suscripción del portal de pagos, en cualquier momento. La cancelación queda reflejada en el estado de la suscripción en la pestaña Suscriptores.",
    paths: [SUBSCRIPTIONS],
  },
  {
    id: "reporte-plus",
    q: "¿Dónde veo los cobros de las suscripciones?",
    a: "Descarga el Reporte de Pagos de esta sección: trae el detalle de cada cobro de suscripción por inquilino y fecha.",
    paths: [SUBSCRIPTIONS],
  },
  {
    id: "enlace-de-pagos",
    q: "¿Cómo comparto el enlace de pagos con los inquilinos?",
    a: "Usa Copiar enlace de pagos en el menú lateral y ponlo en tu sitio web o en tus mensajes. El inquilino entra con su documento y ve lo que tiene pendiente.",
    paths: [],
  },
  {
    id: "medios-de-pago",
    q: "¿Con qué medios pueden pagar los inquilinos?",
    a: "PSE, tarjeta de crédito, Nequi, Botón Bancolombia y Bre-B, según lo que tengas activo. Un administrador lo ajusta en Configuración, Métodos de pago.",
    paths: [],
  },
  {
    id: "agregar-usuarios",
    q: "¿Cómo agrego usuarios de mi equipo?",
    a: "Un administrador los invita en Configuración, Usuarios, y les asigna el rol.",
    paths: [],
  },
  {
    id: "cambiar-cuenta-bancaria",
    q: "¿Cómo cambio la cuenta bancaria donde recibo los abonos?",
    a: "En Configuración, Cuenta bancaria propones el cambio y Palomma lo revisa y aprueba antes de aplicarlo.",
    paths: [],
  },
  {
    id: "tutoriales",
    q: "¿Dónde encuentro tutoriales del portal?",
    a: "En Tutoriales hay videos del portal administrativo, del portal de pagos y de la carga de recaudos.",
    paths: [],
    cta: { label: "Ver tutoriales", to: TUTORIALS },
  },
];

const SECTION_BY_PATH: Record<string, string> = {
  [HOME]: "Resumen",
  [CUSTOMERS]: "Clientes",
  [INVOICES]: "Recaudos",
  [SETTLEMENTS]: "Conciliación",
  [WHATSAPP]: "WhatsApp",
  [SUBSCRIPTIONS]: "Suscripciones",
  [PAYOUTS]: "Pagos",
  [BILLING]: "Facturación",
};

export const faqSection = (pathname: string): string | undefined =>
  Object.entries(SECTION_BY_PATH).find(([p]) => pathname.startsWith(p))?.[1];

const norm = (s: string): string =>
  s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

export const faqsFor = (pathname: string, query: string): Faq[] => {
  const q = norm(query.trim());
  if (q) return FAQS.filter((f) => norm(`${f.q} ${f.a}`).includes(q));
  const own = FAQS.filter((f) => f.paths.some((p) => pathname.startsWith(p)));
  return own.length ? own : FAQS.filter((f) => f.paths.length === 0);
};
